import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import CriticalCSS from './components/CriticalCSS';
import EnhancedCanonicalManager from './components/EnhancedCanonicalManager';
import A11yManager from './components/A11yManager';
import ContrastFixer from './components/ContrastFixer';
import RemoveNewOrderLink from './utils/RemoveNewOrderLink';
import { ThemeProvider } from './context/ThemeContext';
import { Helmet } from 'react-helmet';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './styles/App.scss';
import './styles/AccessibilityContrast.scss';
import DebugApi from './pages/DebugApi';
// Import contact components (not lazy loaded since they're critical)
import TopContactBar from './components/TopContactBar';
import StickyContactOptions from './components/StickyContactOptions';

// Lazy load components that aren't needed immediately
const LazyHome = lazy(() => import('./pages/Home'));
const LazyServices = lazy(() => import('./pages/Services'));
const LazyHowItWorks = lazy(() => import('./pages/HowItWorks'));
const LazyContact = lazy(() => import('./pages/Contact'));
const LazyAffiliate = lazy(() => import('./pages/Affiliate'));
const LazyOrder = lazy(() => import('./pages/Order'));
const LazyPrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const LazyRefundGuarantee = lazy(() => import('./pages/RefundGuarantee'));
const LazyTermsConditions = lazy(() => import('./pages/TermsConditions'));
const LazyCookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const LazyFAQ = lazy(() => import('./pages/FAQ'));
const LazyBlog = React.lazy(() => import('./pages/Blog'));

const LazyBlogPost = lazy(() => import('./pages/BlogPost'));
const LazySummarizer = lazy(() => import('./pages/Summarizer'));
const LazyPaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const LazyPaymentCancel = lazy(() => import('./pages/PaymentCancel'));

const LazyResearchPaperWritingServicePage = lazy(() => import('./pages/ResearchPaperWritingService'));
const LazyRStudioAssignmentHelp = lazy(() => import('./pages/RStudioAssignmentHelp'));
const LazySomeoneToTakeMyOnlineClass = lazy(() => import('./pages/SomeoneToTakeMyOnlineClass'));
const LazyCapellaUniversityOnlineCourses = lazy(() => import('./pages/CapellaUniversityOnlineCourses'));
const LazyProgrammingAssignmentHelpServices = lazy(() => import('./pages/ProgrammingAssignmentHelpServices'));
const LazyBestPythonAssignmentHelp = lazy(() => import('./pages/BestPythonAssignmentHelp'));
const LazyDoMyJavaAssignment = lazy(() => import('./pages/DoMyJavaAssignment'));
const LazyDoMyExcelAssignmentForMe = lazy(() => import('./pages/DoMyExcelAssignmentForMe'));
const LazyDoMyPhysicsExamOnline = lazy(() => import('./pages/DoMyPhysicsExamOnline'));
const LazyDoMyHonorlockExam = lazy(() => import('./pages/DoMyHonorlockExam'));
const LazyDoMyStatisticsHonorlockExam = lazy(() => import('./pages/DoMyStatisticsHonorlockExam'));
const LazyDoMyAnatomyHonorlockExam = lazy(() => import('./pages/DoMyAnatomyHonorlockExam'));
const LazyDoMyRespondusLockdownBrowserExam = lazy(() => import('./pages/DoMyRespondusLockdownBrowserExam'));
const LazyCalculusHonorlockExamHelp = lazy(() => import('./pages/CalculusHonorlockExamHelp'));
const LazyDoMyProctoredExam = lazy(() => import('./pages/DoMyProctoredExam'));
const LazyTakeProctoredExam = lazy(() => import('./pages/TakeProctoredExam'));
const LazyCodingAssignmentHelpServices = lazy(() => import('./pages/CodingAssignmentHelpServices'));
const LazyPowerPointPresentationService = lazy(() => import('./pages/PowerPointPresentationService'));
const LazyDissertationWritingService = lazy(() => import('./pages/DissertationWritingService'));
const LazyMathAssignmentHelpService = lazy(() => import('./pages/MathAssignmentHelpService'));
const LazyDoMyCalculusHomework = lazy(() => import('./pages/DoMyCalculusHomework'));
const LazyStatisticsClassHelper = lazy(() => import('./pages/StatisticsClassHelper'));
const LazyStatisticsAssignmentHelpService = lazy(() => import('./pages/StatisticsAssignmentHelpService'));
const LazySPSSAssignmentHelp = lazy(() => import('./pages/SPSSAssignmentHelp'));
const LazyDatabaseManagementService = lazy(() => import('./pages/DatabaseManagementService'));
const LazyDatabaseAssignmentHelpService = lazy(() => import('./pages/DatabaseAssignmentHelpService'));
const LazyAffordableAccountingClassHelp = lazy(() => import('./pages/AffordableAccountingClassHelp'));
const LazyAffordableAccountingAssignmentHelp = lazy(() => import('./pages/AffordableAccountingAssignmentHelp'));
const LazyTakeMyOnlineChemistryClassForMe = lazy(() => import('./pages/TakeMyOnlineChemistryClassForMe'));
const LazyTakeMyOnlineBiologyClass = lazy(() => import('./pages/TakeMyOnlineBiologyClass'));
const LazyPhysicsClassHelpService = lazy(() => import('./pages/PhysicsClassHelpService'));
const LazyPhysicsHomeworkHelpService = lazy(() => import('./pages/PhysicsHomeworkHelpService'));
const LazyUrgentEssayWritingHelp = lazy(() => import('./pages/UrgentEssayWritingHelp'));
const LazyHomeworkHelpService = lazy(() => import('./pages/HomeworkHelpService'));
const LazyHireSomeoneToTakeMyExam = lazy(() => import('./pages/HireSomeoneToTakeMyExam'));
const LazyHireSomeoneToTakeMyHistoryExam = lazy(() => import('./pages/HireSomeoneToTakeMyHistoryExam'));
const LazyOnlineClassHelpServices = lazy(() => import('./pages/OnlineClassHelpServices'));
const LazyOnlineCourseHelp = lazy(() => import('./pages/OnlineCourseHelp'));
const LazyWriteMyCaseStudy = lazy(() => import('./pages/WriteMyCaseStudy'));
const LazyLastMinuteEssayHelp = lazy(() => import('./pages/LastMinuteEssayHelp'));
const LazyAffordableDissertationWriters = lazy(() => import('./pages/AffordableDissertationWriters'));
const LazyUrgentCalculusAssignmentHelpService = lazy(() => import('./pages/UrgentCalculusAssignmentHelpService'));

const LazyUrgentAnnotatedBibliographyService = lazy(() => import('./pages/UrgentAnnotatedBibliographyService'));
const LazyHireSomeoneToTakeYourOnlineCourse = lazy(() => import('./pages/HireSomeoneToTakeYourOnlineCourse'));
const LazyTakeMyUTAOnlineNursingClass = lazy(() => import('./pages/TakeMyUTAOnlineNursingClass'));
const LazyHelpWithUTARNToBSNAssignments = lazy(() => import('./pages/HelpWithUTARNToBSNAssignments'));
const LazyHireSomeoneToDoMyCalculusClass = lazy(() => import('./pages/HireSomeoneToDoMyCalculusClass'));
const LazyTakeMyOnlineAlgebraClass = lazy(() => import('./pages/TakeMyOnlineAlgebraClass'));
const LazyUMPIOnlineClassHelp = lazy(() => import('./pages/UMPIOnlineClassHelp'));
const LazyUMGCOnlineClassHelp = lazy(() => import('./pages/UMGCOnlineClassHelp'));
const LazyASUOnlineClassHelp = lazy(() => import('./pages/ASUOnlineClassHelp'));
const LazyTakeMyWGUClass = lazy(() => import('./pages/TakeMyWGUClass'));
const LazyTakeMyWGUCourses = lazy(() => import('./pages/TakeMyWGUCourses'));
const LazyTakeMyProctorioExam = lazy(() => import('./pages/TakeMyProctorioExam'));
const LazySNHUOnlineClassHelp = lazy(() => import('./pages/SNHUOnlineClassHelp'));
const LazySomeoneToTakeMySophiaLearningClasses = lazy(() => import('./pages/SomeoneToTakeMySophiaLearningClasses'));
const LazyTakeMyGMATExam = lazy(() => import('./pages/TakeMyGMATExam'));

const LazyPaySomeoneToWriteMyEssayCheap = lazy(() => import('./pages/PaySomeoneToWriteMyEssayCheap'));
const LazyHireSomeoneToTakeMyStatisticsClass = lazy(() => import('./pages/HireSomeoneToTakeMyStatisticsClass'));
const LazyNursingEssayWritingService = lazy(() => import('./pages/NursingEssayWritingService'));
const LazyAutoCADAssignmentHelp = lazy(() => import('./pages/AutoCADAssignmentHelp'));
const LazyDoMyComputerScienceAssignment = lazy(() => import('./pages/DoMyComputerScienceAssignment'));
const LazySamplePapers = lazy(() => import('./pages/SamplePapers'));
const LazyNotFound = lazy(() => import('./pages/NotFound'));
const LazyAdminSamplePapers = lazy(() => import('./pages/AdminSamplePapers'));
const LazyAdminChatDashboard = lazy(() => import('./pages/AdminChatDashboard'));
const LazyAccessibilityExamples = lazy(() => import('./components/AccessibilityExamples'));
const LazyContrastDemo = lazy(() => import('./components/ContrastDemo'));

// Lazy load components that are not needed on initial render
const LazyLiveChat = lazy(() => import('./components/LiveChat'));
const LazyCookieBanner = lazy(() => import('./components/CookieBanner'));

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div 
    className="page-loader" 
    role="status"
    aria-live="polite"
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100%'
    }}
  >
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading content, please wait...</span>
    </div>
    <p className="sr-only">Loading content, please wait...</p>
  </div>
);

// ScrollToTop component to reset scroll position when navigating
const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// ReferralCapture component to capture referral codes from URLs site-wide
const ReferralCapture = () => {
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref') || urlParams.get('referral');
    
    if (refCode) {
      const code = refCode.toUpperCase();
      localStorage.setItem('hwg_referral_code', code);
      console.log('Referral code captured and saved:', code);
    }
  }, []);

  return null;
};

// AdminRouteChecker component to conditionally render components for non-admin routes
const AdminRouteChecker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Don't render children for admin routes
  if (isAdminRoute) {
    return null;
  }

  return <>{children}</>;
};

// ChatRouteChecker component to conditionally render chat components (hide on blog pages)
const ChatRouteChecker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isBlogPage = location.pathname.startsWith('/blog') || location.pathname.includes('/blog/');

  // Don't render chat components on blog pages
  if (isBlogPage) {
    return null;
  }

  return <>{children}</>;
};

// Global app helpers
const AppHelpers: React.FC = () => {
  return (
    <>
      <ScrollToTop />
      <ReferralCapture />
      <RemoveNewOrderLink />
      <EnhancedCanonicalManager />
      <CriticalCSS />
      <ContrastFixer />
      <Helmet>
        {/* Explicitly tell search engines to index the site and follow links */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Enhanced resource hints for better performance */}
        <link rel="preload" href="/images/logoheader.svg" as="image" fetchPriority="high" />
        <link rel="preload" href="/fonts/main-font.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
        <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://www.google-analytics.com" />
        {/* Performance optimization meta tags */}
        <meta httpEquiv="x-dns-prefetch-control" content="on" />
      </Helmet>
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <A11yManager>
        <AppHelpers />
        <div className="App">
            {/* Only show header components for non-admin routes */}
            <AdminRouteChecker>
              <TopContactBar />
              <Navigation />
            </AdminRouteChecker>
            <main className="main-content" id="main-content" tabIndex={-1}>
              <Suspense fallback={<PageLoader />}>
            <Routes>
                  <Route path="/" element={<LazyHome />} />
                  <Route path="services" element={<LazyServices />} />
                  <Route path="how-it-works" element={<LazyHowItWorks />} />
                  <Route path="contact" element={<LazyContact />} />
                  <Route path="affiliate" element={<LazyAffiliate />} />
                  <Route path="order" element={<LazyOrder />} />
                  <Route path="privacy-policy" element={<LazyPrivacyPolicy />} />
                  <Route path="terms-conditions" element={<LazyTermsConditions />} />
                  <Route path="cookie-policy" element={<LazyCookiePolicy />} />
                  <Route path="refund-guarantee" element={<LazyRefundGuarantee />} />
                  <Route path="faq" element={<LazyFAQ />} />
                  <Route path="blog" element={<LazyBlog />} />
                  <Route path="blog/:slug" element={<LazyBlogPost />} />
                  <Route path="summarizer" element={<LazySummarizer />} />
                  <Route path="payment-success" element={<LazyPaymentSuccess />} />
                  <Route path="payment-cancel" element={<LazyPaymentCancel />} />

                  <Route path="/research-paper-writing-service" element={<LazyResearchPaperWritingServicePage />} />
                  <Route path="/r-studio-assignment-help" element={<LazyRStudioAssignmentHelp />} />
                  <Route path="someone-to-take-my-online-class" element={<LazySomeoneToTakeMyOnlineClass />} />
                  <Route path="/take-my-capella-university-online-courses" element={<LazyCapellaUniversityOnlineCourses />} />
                  <Route path="/programming-assignment-help-service" element={<LazyProgrammingAssignmentHelpServices />} />
                  <Route path="/best-python-assignment-help" element={<LazyBestPythonAssignmentHelp />} />
                  <Route path="/do-my-java-assignment" element={<LazyDoMyJavaAssignment />} />
                  <Route path="/do-my-java-assignments" element={<LazyDoMyJavaAssignment />} />
                  <Route path="/do-my-excel-assignment-for-me" element={<LazyDoMyExcelAssignmentForMe />} />
                  <Route path="/do-my-physics-exam-online" element={<LazyDoMyPhysicsExamOnline />} />
                  <Route path="/do-my-honorlock-exam" element={<LazyDoMyHonorlockExam />} />
                  <Route path="/do-my-statistics-honorlock-exam" element={<LazyDoMyStatisticsHonorlockExam />} />
                  <Route path="/do-my-anatomy-honorlock-exam" element={<LazyDoMyAnatomyHonorlockExam />} />
                  <Route path="/do-my-respondus-lockdown-browser-exam" element={<LazyDoMyRespondusLockdownBrowserExam />} />
                  <Route path="/calculus-honorlock-exam-help" element={<LazyCalculusHonorlockExamHelp />} />
                  <Route path="/do-my-proctored-exam-for-me" element={<LazyDoMyProctoredExam />} />
                  <Route path="/take-proctored-exam" element={<LazyTakeProctoredExam />} />
                  <Route path="/coding-assignment-help-services" element={<LazyCodingAssignmentHelpServices />} />
                  <Route path="/powerpoint-presentation-service" element={<LazyPowerPointPresentationService />} />
                  <Route path="/dissertation-writing-service" element={<LazyDissertationWritingService />} />
                  <Route path="/math-assignment-help-service" element={<LazyMathAssignmentHelpService />} />
                  <Route path="/do-my-calculus-homework" element={<LazyDoMyCalculusHomework />} />
                  <Route path="/statistics-class-helper" element={<LazyStatisticsClassHelper />} />
                  <Route path="/statistics-assignment-help-service" element={<LazyStatisticsAssignmentHelpService />} />
                  <Route path="/spss-assignment-help" element={<LazySPSSAssignmentHelp />} />
                  <Route path="/database-management-service" element={<LazyDatabaseManagementService />} />
                  <Route path="/database-assignment-help-service" element={<LazyDatabaseAssignmentHelpService />} />
                  <Route path="/affordable-accounting-class-help" element={<LazyAffordableAccountingClassHelp />} />
                  <Route path="/affordable-accounting-assignment-help" element={<LazyAffordableAccountingAssignmentHelp />} />
                  <Route path="/take-my-online-chemistry-class-for-me" element={<LazyTakeMyOnlineChemistryClassForMe />} />
                  <Route path="/take-my-online-biology-class" element={<LazyTakeMyOnlineBiologyClass />} />
                  <Route path="/physics-class-help-service" element={<LazyPhysicsClassHelpService />} />
                  <Route path="/physics-homework-help-service" element={<LazyPhysicsHomeworkHelpService />} />
                  <Route path="/urgent-essay-writing-help" element={<LazyUrgentEssayWritingHelp />} />
                  <Route path="/homework-help-service" element={<LazyHomeworkHelpService />} />
                  <Route path="/hire-someone-to-take-my-exam" element={<LazyHireSomeoneToTakeMyExam />} />
                  <Route path="/hire-someone-to-take-my-history-exam" element={<LazyHireSomeoneToTakeMyHistoryExam />} />
                  <Route path="/online-class-help-services" element={<LazyOnlineClassHelpServices />} />
                  <Route path="/online-course-help" element={<LazyOnlineCourseHelp />} />
                  <Route path="/write-my-case-study" element={<LazyWriteMyCaseStudy />} />
                  <Route path="/last-minute-essay-help" element={<LazyLastMinuteEssayHelp />} />
                  <Route path="/affordable-dissertation-writers" element={<LazyAffordableDissertationWriters />} />
                  <Route path="/urgent-calculus-assignment-help-service" element={<LazyUrgentCalculusAssignmentHelpService />} />
        
                  <Route path="/urgent-annotated-bibliography-service" element={<LazyUrgentAnnotatedBibliographyService />} />
                  <Route path="/hire-someone-to-take-your-online-course" element={<LazyHireSomeoneToTakeYourOnlineCourse />} />
                  <Route path="/take-my-uta-online-nursing-class" element={<LazyTakeMyUTAOnlineNursingClass />} />
                  <Route path="/help-with-uta-rn-to-bsn-assignments" element={<LazyHelpWithUTARNToBSNAssignments />} />
                  <Route path="/hire-someone-to-do-my-calculus-class" element={<LazyHireSomeoneToDoMyCalculusClass />} />
                  <Route path="/take-my-online-algebra-class" element={<LazyTakeMyOnlineAlgebraClass />} />
                  <Route path="/umpi-online-class-help" element={<LazyUMPIOnlineClassHelp />} />
                  <Route path="/umgc-online-class-help" element={<LazyUMGCOnlineClassHelp />} />
                  <Route path="/asu-online-class-help" element={<LazyASUOnlineClassHelp />} />
                  <Route path="/take-my-wgu-class" element={<LazyTakeMyWGUClass />} />
                  <Route path="/take-my-wgu-courses" element={<LazyTakeMyWGUCourses />} />
                  <Route path="/take-my-proctorio-exam" element={<LazyTakeMyProctorioExam />} />
                  <Route path="/someone-to-take-my-snhu-online-class" element={<LazySNHUOnlineClassHelp />} />
                  <Route path="/someone-to-take-my-sophia-learning-classes" element={<LazySomeoneToTakeMySophiaLearningClasses />} />
                  <Route path="/someone-to-take-my-gmat-exam" element={<LazyTakeMyGMATExam />} />

                  <Route path="/pay-someone-to-write-my-essay-cheap" element={<LazyPaySomeoneToWriteMyEssayCheap />} />
                  <Route path="/hire-someone-to-take-my-statistics-class" element={<LazyHireSomeoneToTakeMyStatisticsClass />} />
                  <Route path="/nursing-essay-writing-service" element={<LazyNursingEssayWritingService />} />
                  <Route path="/autocad-assignment-help" element={<LazyAutoCADAssignmentHelp />} />
                  <Route path="/do-my-computer-science-assignment" element={<LazyDoMyComputerScienceAssignment />} />
                  <Route path="/sample-papers" element={<LazySamplePapers />} />
                  <Route path="/admin/sample-papers" element={<LazyAdminSamplePapers />} />
                  <Route path="/admin/chat" element={<LazyAdminChatDashboard />} />
                  <Route path="/a11y" element={<LazyAccessibilityExamples />} />
                  <Route path="/contrast" element={<LazyContrastDemo />} />
                  <Route path="/debug-api" element={<DebugApi />} />
                  {/* Catch-all route for 404 - must be last */}
                  <Route path="*" element={<LazyNotFound />} />
            </Routes>
              </Suspense>
          </main>
            {/* Only show footer and contact components for non-admin routes */}
            <AdminRouteChecker>
              <Footer />
              {/* Lazy load non-critical UI elements */}
              <Suspense fallback={null}>
                <ChatRouteChecker>
                  <LazyLiveChat />
                </ChatRouteChecker>
                <LazyCookieBanner />
              </Suspense>
              {/* Contact components - hide on blog pages */}
              <ChatRouteChecker>
                <StickyContactOptions />
              </ChatRouteChecker>
            </AdminRouteChecker>
        </div>
        </A11yManager>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
